import React from 'react';

import {
  ALBUM_SUBTYPE,
  ALBUM_SUBTYPE_WALDOFIED_LABEL,
} from '../../../../constants/albums';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../constants/event-items';
import { entityRequiresVGBContext } from '../../../../util/album';
import { getAsset } from '../../../../util/assets';

const baseFeatures = [
  'Unlimited invites & contributors',
  '10GB of photo & video storage',
  'Web, iOS, and Android access',
];

const getAddonItemDetails = eventType => {
  const gvHowItWorksAsset = (() => {
    if (entityRequiresVGBContext({ album_subtype: eventType })) {
      return eventType === ALBUM_SUBTYPE.MEMORIALS
        ? getAsset('/vgb-memorials-how-it-works.gif')
        : getAsset('/vgb-how-it-works.gif');
    }

    return getAsset('/gv-how-it-works.gif');
  })();

  return {
    [EVENT_ADDON_ITEM_TYPE.WALDOFIED]: {
      reviewTitle: 'Gold with Live Slideshow',
      title: (
        <>
          Waldo <span className="text-gold ml-1">Gold</span>{' '}
        </>
      ),
      description:
        'Gold comes with these premium features sure to make your event memorable:',
      features: [
        {
          id: 'LIVE_SLIDESHOWS',
          label: `Live ${ALBUM_SUBTYPE_WALDOFIED_LABEL[eventType]} smart slideshow`,
        },
        'Printable QR code signage',
        'Personalized SMS code for easy guests onboarding',
        {
          id: 'PHOTO_DELIVERY',
          label: 'Automated photo delivery to guests',
        },
        'Photo Moderation',
        'Bulk guest list import & invites',
        eventType === ALBUM_SUBTYPE.WEDDINGS
          ? undefined
          : 'Unlimited Sub-albums',
        '50GB of photo & video storage',
      ].filter(Boolean),
    },
    [EVENT_ADDON_ITEM_TYPE.WALDOFIED_PRO]: {
      reviewTitle: 'Gold (Single day event)',
      title: (
        <>
          Waldo <span className="text-gold ml-1">Gold</span>{' '}
        </>
      ),
      description:
        'Gold comes with these premium features sure to make your event memorable:',
      features: [
        {
          id: 'LIVE_SLIDESHOWS',
          label: `Live ${ALBUM_SUBTYPE_WALDOFIED_LABEL[eventType]} smart slideshow`,
        },
        'Printable QR code signage',
        'Personalized SMS code for easy guests onboarding',
        {
          id: 'PHOTO_DELIVERY',
          label: 'Automated photo delivery to guests',
        },
        'Photo Moderation',
        'Bulk guest list import & invites',
        eventType === ALBUM_SUBTYPE.WEDDINGS
          ? undefined
          : 'Unlimited Sub-albums',
        '50GB of photo & video storage',
      ].filter(Boolean),
    },
    [EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK]: {
      title: 'Memories Smart Slideshow',
      description:
        'Waldo will help you easily gather old photos from friends & family and then automatically organize them into a beautiful slideshow!',
      features: [],
      howItWorks: {
        header: 'How it works:',
        assetUrl: getAsset('/memory-book-how-it-works.gif'),
      },
    },
    [EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO]: {
      title: entityRequiresVGBContext({ album_subtype: eventType })
        ? 'Video Guestbook'
        : 'Group Video',
      description: entityRequiresVGBContext({ album_subtype: eventType })
        ? 'Want to celebrate with a Video Guestbook? Let Waldo help you gather 30 second video clips from friends & family and automatically turn them into a video montage'
        : 'Want to celebrate with a group video? Let Waldo help you gather 30 second video clips from friends & family and automatically turn them into a video montage',
      features: [],
      howItWorks: {
        header: 'How it works:',
        assetUrl: gvHowItWorksAsset,
      },
    },
    [`${EVENT_ADDON_ITEM_TYPE.WALDOFIED},${EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO},${EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK}`]:
      {
        title: (
          <>
            Gold,{' '}
            {entityRequiresVGBContext({ album_subtype: eventType })
              ? 'Video Guestbook'
              : 'Group Video'}{' '}
            &<br />
            Memories Slideshow
          </>
        ),
        reviewTitle: 'Bundle: Gold + Group Video + Memories Slideshow',
      },
  };
};

export const PRO_FREEMIUM_EVENT_FEATURES = [
  'Printable QR code signage',
  'Brand filter and hashtag',
  'Accessible on iOS, Android, and web',
  'Unlimited invites & contributors',
  'Automated photo delivery to guests',
  'View & manage gallery access',
  'SMS code for easy joining',
  'Unlimited sub-albums',
  'Live slideshow',
  'Up to 50GB of storage for 1 year',
];

export const FREEMIUM_EVENTS_META = {
  [ALBUM_SUBTYPE.BIRTHDAYS]: {
    icon: '/birthday-event-icon.png',
    title: 'Turn this into your best birthday celebration yet!',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.BIRTHDAYS),
  },
  [ALBUM_SUBTYPE.ANNIVERSARIES]: {
    icon: '/anniversary-event-icon.png',
    title: 'Gather, share and celebrate the memories made!',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.ANNIVERSARIES),
  },
  [ALBUM_SUBTYPE.GRADUATIONS]: {
    icon: '/graduation-event-icon.png',
    title: 'Gather, share and celebrate memories and a milestone!',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.GRADUATIONS),
  },
  [ALBUM_SUBTYPE.MEMORIALS]: {
    icon: '/memorial-event-icon.png',
    title: 'Gather, share and celebrate a life that touched so many! ❤',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.MEMORIALS),
  },
  [ALBUM_SUBTYPE.VACATIONS]: {
    icon: '/vacation-event-icon.png',
    title: "Everyone's vacay memories easily gathered and shared!",
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.VACATIONS),
  },
  [ALBUM_SUBTYPE.FUNDRAISERS]: {
    icon: '/fundraiser-event-icon.png',
    title: 'Waldo-fy your next fundraiser!',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.FUNDRAISERS),
  },
  [ALBUM_SUBTYPE.CUSTOM_EVENT]: {
    icon: '/custom-event-icon.png',
    title: 'Waldo-fy your next event!',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.CUSTOM_EVENT),
  },
  [ALBUM_SUBTYPE.WEDDINGS]: {
    icon: '/wedding-event-icon.png',
    title: 'Gather, share and celebrate the memories made!',
    baseFeatures: [...baseFeatures],
    addonItemDetails: getAddonItemDetails(ALBUM_SUBTYPE.WEDDINGS),
  },
};

/**
 * Returns the total sum of event addon items (raw).
 * Use this only when the albumFolderId is unavailable, since checkoutPrice requires
 * an albumFolderId for purchase.
 *
 * @param {Object} opts
 * @param {Array<Object>} opts.eventAddonItems List of event addon items to calculate total price for.
 * @returns {Number}
 */
export const getRawEventAddonItemsPrice = ({ eventAddonItems }) => {
  return eventAddonItems.reduce((acc, eventAddonItem) => {
    return acc + (eventAddonItem?.price?.value ?? 0);
  }, 0);
};

export const getEventAddonItemsByType = (
  eventAddonItems = [],
  eventAddonItemType,
) => {
  return eventAddonItems.filter(eventAddonItem =>
    eventAddonItem.types.includes(eventAddonItemType),
  );
};
